<script setup lang="ts">
import { ref, watch } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxDateBox } from 'devextreme-vue/date-box';
import { BottoPagePanel, JsonEditor, bottoGridPaging } from '@botto/core';
import CdpPageShell from '../components/CdpPageShell.vue';
import { postbacksStore, setPostbackListFilters, type Postback } from '../api';
import { useCdpProjectContext } from '../composables/useCdpProjectContext';

const { paging, pager } = bottoGridPaging();

const { hasProject, selectedProjectId } = useCdpProjectContext();

const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const detailOpen = ref(false);
const detailRow = ref<Postback | null>(null);

const filterStart = ref<Date | null>(null);
const filterEnd = ref<Date | null>(null);

function applyFilters() {
  setPostbackListFilters({
    start_date: filterStart.value ? filterStart.value.toISOString() : null,
    end_date: filterEnd.value ? filterEnd.value.toISOString() : null,
  });
  grid.value?.instance?.refresh();
}

function openDetail(e: { data: Postback }) {
  detailRow.value = e.data;
  detailOpen.value = true;
}

watch(selectedProjectId, () => {
  if (hasProject.value) grid.value?.instance?.refresh();
});
</script>

<template>
  <CdpPageShell title="События">
    <BottoPagePanel>
      <div class="events-filters">
        <label>
          С
          <DxDateBox v-model:value="filterStart" type="datetime" />
        </label>
        <label>
          По
          <DxDateBox v-model:value="filterEnd" type="datetime" />
        </label>
        <button type="button" class="events-filters__btn" @click="applyFilters">Применить</button>
      </div>

      <DxDataGrid
        ref="grid"
        :data-source="postbacksStore"
        :remote-operations="true"
        :show-borders="false"
        :show-row-lines="false"
        :show-column-lines="false"
        :row-alternation-enabled="true"
        :column-auto-width="true"
        :hover-state-enabled="true"
        :paging="paging"
        :pager="pager"
        height="100%"
        width="100%"
        @row-click="openDetail"
      >
        <DxScrolling mode="standard" />
        <DxSorting mode="single" />

        <DxColumn data-field="goal" caption="Goal" />
        <DxColumn data-field="phone" caption="Телефон" />
        <DxColumn data-field="click_id" caption="Click ID" />
        <DxColumn data-field="project_id" caption="Project ID" :visible="false" />
        <DxColumn data-field="created_at" caption="Создано" data-type="datetime" width="180" />
      </DxDataGrid>
    </BottoPagePanel>

    <DxPopup
      v-model:visible="detailOpen"
      title="Событие"
      :width="720"
      :height="560"
      :show-close-button="true"
    >
      <template #content>
        <JsonEditor v-if="detailRow" :model-value="detailRow" height="460px" readonly />
      </template>
      <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="{ text: 'Закрыть', onClick: () => (detailOpen = false) }" />
    </DxPopup>
  </CdpPageShell>
</template>

<style scoped>
.events-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
  flex: 0 0 auto;
}
.events-filters label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.875rem;
}
.events-filters__btn {
  padding: 8px 16px;
  cursor: pointer;
}
</style>
